import { useEffect, useMemo, useState } from 'react';

import { usePreferences } from '@/react-query/usePreferences';
import { useVoiceNoteTemplates } from '@/react-query/videa-notes/useVoiceNoteTemplates';

import type { VoiceNoteTemplateOption } from '../types';

/**
 * Hook for managing template selection in Videa Notes.
 * Handles loading templates from the API and auto-selecting the first template.
 */
export const usePromptTemplateSelection = () => {
  const { data: voiceNoteTemplates = [] } = useVoiceNoteTemplates();
  const { data: preferences } = usePreferences();
  const voiceNotesTemplatesConfig = preferences?.voiceNotesTemplatesConfig;

  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(
    undefined,
  );

  const templateOptions: VoiceNoteTemplateOption[] = useMemo(
    () =>
      voiceNoteTemplates.map((template) => ({
        id: template.id,
        name: template.name,
        type: template.type,
        userInstructions: template.userInstructions,
      })),
    [voiceNoteTemplates],
  );

  // Auto-select the first template when templates load from the API
  useEffect(() => {
    if (templateOptions.length === 0) return;

    const defaultId = voiceNotesTemplatesConfig?.defaultTemplateId;
    const fallbackId = templateOptions[0].id;

    const selectedId =
      defaultId && templateOptions.find((t) => t.id === defaultId)?.id
        ? defaultId
        : fallbackId;

    setSelectedTemplate((prev) => (prev === selectedId ? prev : selectedId));
  }, [templateOptions, voiceNotesTemplatesConfig]);

  return {
    templateOptions,
    selectedTemplate,
    setSelectedTemplate,
  };
};
